import type { useToast } from "~/context/ToastContext";
import { getMeetOrigins } from "~/env";
import { sanitizeUrlForBreadcrumb } from "~/lib/safe-url";
import { openExternal } from "~/lib/platform";
import { recordSdkBreadcrumb } from "~/lib/telemetry";

export type MeetingUrlRejectReason = "malformed" | "scheme" | "auth" | "ip-literal" | "host";

export class MeetingUrlNotAllowedError extends Error {
  constructor(public readonly reason: MeetingUrlRejectReason) {
    super(`meeting url rejected: ${reason}`);
    this.name = "MeetingUrlNotAllowedError";
  }
}

const IP_LITERAL = /^(?:(?:\d{1,3}\.){3}\d{1,3}|\[[0-9a-fA-F:.]+\])$/;

export function validateMeetingUrl(url: string, allowedOrigins: readonly string[]): URL {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new MeetingUrlNotAllowedError("malformed");
  }
  if (parsed.protocol !== "https:") throw new MeetingUrlNotAllowedError("scheme");
  if (parsed.username !== "" || parsed.password !== "") {
    throw new MeetingUrlNotAllowedError("auth");
  }
  if (IP_LITERAL.test(parsed.hostname)) throw new MeetingUrlNotAllowedError("ip-literal");
  // Hostname-only validation intentionally strips ports; preview meet hosts may use non-standard ports.
  const hostname = parsed.hostname.toLowerCase();
  const allowList = allowedOrigins.map((origin) => origin.toLowerCase());
  if (!allowList.includes(hostname)) throw new MeetingUrlNotAllowedError("host");
  return parsed;
}

export async function openMeeting(
  resolvedUrl: string,
  pushToast: ReturnType<typeof useToast>["push"],
  allowedOrigins: readonly string[] = getMeetOrigins(),
): Promise<void> {
  try {
    const url = validateMeetingUrl(resolvedUrl, allowedOrigins);
    await openExternal(url.toString());
  } catch (err) {
    if (err instanceof MeetingUrlNotAllowedError) {
      recordSdkBreadcrumb("matrix.meeting.url-rejected", "rejected meeting URL", err, {
        sanitisedUrl: sanitizeUrlForBreadcrumb(resolvedUrl),
        reason: err.reason,
      });
      pushToast({ message: "This meeting link looks unsafe and was not opened.", tone: "danger" });
      return;
    }
    throw err;
  }
}
